import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { NavigationProduct, Tag } from '../../shared/models/product.model';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private tags: Tag[] = [];
  private tagColors: string[] = ['#4287f5', '#42f59e', '#f54242', '#f5a442', '#9e42f5', '#42d7f5', '#f59242', '#42f542', '#5442f5', '#f542a4'];

  /**
   * Extract and collect all unique tags from products
   */
  private extractTagsFromProducts(products: NavigationProduct[]): Tag[] {
    const tagMap = new Map<number, Tag>();

    const processProduct = (product: NavigationProduct) => {
      if (product.tags) {
        product.tags.forEach((tag) => {
          if (!tagMap.has(tag.id)) {
            tagMap.set(tag.id, {
              ...tag,
              color: tag.color,
            });
          }
        });
      }

      // Process child products recursively
      if (product.childProducts) {
        product.childProducts.forEach((childProduct) => {
          processProduct(childProduct);
        });
      }
    };

    products.forEach((product) => processProduct(product));

    // Convert to array and assign colors to avoid adjacent duplicates
    const tagsArray = Array.from(tagMap.values());

    // Assign colors ensuring no adjacent tags have the same color
    return tagsArray.map((tag, index) => ({
      ...tag,
      color: tag.color || this.tagColors[index % this.tagColors.length],
    }));
  }

  /**
   * Apply colors to tags in products
   */
  private applyColorsToProductTags(products: NavigationProduct[]): NavigationProduct[] {
    const processProduct = (product: NavigationProduct): NavigationProduct => {
      const updatedProduct = { ...product };

      if (updatedProduct.tags) {
        updatedProduct.tags = updatedProduct.tags.map((tag) => {
          // Find the tag in our extracted tags to get the consistent color
          const extractedTag = this.tags.find((t) => t.id === tag.id);
          return {
            ...tag,
            color: extractedTag?.color || tag.color || this.tagColors[tag.id % this.tagColors.length],
          };
        });
      }

      if (updatedProduct.childProducts) {
        updatedProduct.childProducts = updatedProduct.childProducts.map((child) => processProduct(child));
      }

      return updatedProduct;
    };

    return products.map((product) => processProduct(product));
  }

  /**
   * Get all available tags from products
   */
  getAllTags(): Observable<Tag[]> {
    return of(this.tags);
  }

  /**
   * Get tags for a specific product
   */
  getTagsForProduct(productId: number, products: NavigationProduct[]): Observable<Tag[]> {
    const product = products.find((p) => p.id === productId);
    if (product && product.tags) {
      return of(product.tags);
    }
    return of([]);
  }

  /**
   * Process products to extract tags and apply colors
   */
  processProductsWithTags(products: NavigationProduct[]): Observable<NavigationProduct[]> {
    // Extract all unique tags from the products
    this.tags = this.extractTagsFromProducts(products);

    // Apply colors to product tags
    const processedProducts = this.applyColorsToProductTags(products);

    return of(processedProducts);
  }

  /**
   * Filter products by tag IDs
   */
  filterProductsByTags(products: NavigationProduct[], tagIds: number[]): Observable<NavigationProduct[]> {
    if (!tagIds.length) {
      return of(products);
    }

    const filteredProducts = products.filter((product) => {
      if (!product.tags || !product.tags.length) {
        return false;
      }
      // Check if product has at least one of the selected tags
      return product.tags.some((tag) => tagIds.includes(tag.id));
    });

    return of(filteredProducts);
  }
}
